import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TableRow, TableCell, Typography, Box } from '@mui/material';
import moment from 'moment-timezone';
import InfoIcon from '@mui/icons-material/Info';

import { FCheckbox, FHTooltip } from '../../../base';
import {
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ORANGE,
  FORTA_WHITE,
  FORTA_SUCCESS,
} from '../../../../utils/colors';
import { toUtcOffsetDisplay } from '../../../../helpers';
import { ClinicalRelationships, Themes } from '../../../../constants';
import {
  SessionClaimStatus,
  ServiceCodes,
  HCPCSServiceCodes,
  AllowedServiceCodesToBuildClaim,
} from './constants';
import {
  formatSessionId,
  getAuthorTagBorder,
  getAuthorTagColor,
  getAuthorTagName,
  getNavigateUrlForProcedures,
  isCredentialLicenseRequired,
  isProviderLicenseInStateOnDate,
  isProviderPayorCredentialsonDate,
} from './utils';
import StatusIconRenderer from '../../StatusIconRenderer';
import { ClaimStatusColor, ClaimStatusFontColor } from '../claim-info/constants';
import { PROCEDURE_DURATION_TO_UNIT_FACTOR } from '../../../../pages/constants';
import { getIsRowDisabled } from './utils/claimSessionTableRow';

import type { RootState } from '../../../../store';
import type { IClientPolicyMap, ISessionWithClaimStatus } from '../../../../utils/types';

interface ClaimSessionTableRowProps {
  session: ISessionWithClaimStatus;
  showSelectColumn: boolean;
  selectedProcedureCode: string;
  historyClaimReviewDialogView: boolean;
  selectedPolicyDetails?: IClientPolicyMap;
  onSelectRow: (selected: boolean, data: ISessionWithClaimStatus) => void;
}

const baseStyles = {
  px: 1,
  py: 0.5,
  width: '40px',
  borderRadius: '8px',
  fontWeight: 700,
  textAlign: 'center',
  color: FORTA_WHITE,
};

const fulfilledStyles = {
  ...baseStyles,
  backgroundColor: FORTA_SUCCESS,
};
const notFulfilledStyles = {
  ...baseStyles,
  backgroundColor: FORTA_ORANGE,
};
const notRequiredStyles = {
  ...baseStyles,
  backgroundColor: FORTA_GRAY4,
};

function isProcedureCode(value: string): value is ServiceCodes | HCPCSServiceCodes {
  return (
    Object.values(ServiceCodes).some((code) => code === value) ||
    Object.values(HCPCSServiceCodes).some((code) => code === value)
  );
}

function isClinicalRelationship(value: string): value is ClinicalRelationships {
  return Object.values(ClinicalRelationships).some((relationship) => relationship === value);
}

const ClaimSessionTableRow = (props: ClaimSessionTableRowProps) => {
  const {
    session,
    session: {
      selected = false,
      id,
      provider_name,
      date: dateInUtc,
      status,
      readonly,
      sessionStatus,
      procedureCode,
      isAwaitingFinalization,
      isUnderClinicalReview,
      bay = '',
      clinical_relationship = '',
      session_provider = '',
      duration = 0,
      author_id,
    },
    showSelectColumn,
    selectedProcedureCode,
    onSelectRow,
    historyClaimReviewDialogView,
    selectedPolicyDetails,
  } = props;

  const {
    providerLicensesMapping,
    providerPayorCredentialsMapping,
    providerLicensesLoading,
    providerPayorCredentialsLoading,
  } = useSelector((state: RootState) => state.providers);

  const [isLicenseRequiredInState, isLicensed] = useMemo(() => {
    let licenseRequired = isCredentialLicenseRequired(clinical_relationship);
    let licensed = false;

    if (!author_id || !selectedPolicyDetails || !providerLicensesMapping || !dateInUtc)
      return [licenseRequired, licensed];

    const licenseDetails =
      providerLicensesMapping[`${author_id}-${selectedPolicyDetails?.billing_payor?.state}`];
    if (licenseDetails) {
      licenseRequired = licenseRequired && licenseDetails.is_license_required_in_state;
      licensed = isProviderLicenseInStateOnDate(licenseDetails, moment.utc(dateInUtc));
    }

    return [licenseRequired, licensed];
  }, [
    providerLicensesMapping,
    providerLicensesLoading,
    author_id,
    selectedPolicyDetails,
    dateInUtc,
  ]);

  const [isCredentialed, isCredentialRequired] = useMemo(() => {
    let credentialed = false;
    const credentialRequired = isCredentialLicenseRequired(clinical_relationship);
    if (!author_id || !selectedPolicyDetails || !providerPayorCredentialsMapping || !dateInUtc)
      return [credentialed, credentialRequired];

    const credentialDetails =
      providerPayorCredentialsMapping[`${author_id}-${selectedPolicyDetails?.billing_payor?.id}`];
    if (credentialDetails) {
      credentialed = isProviderPayorCredentialsonDate(
        credentialDetails,
        moment.utc(dateInUtc),
      );
    }

    return [credentialed, credentialRequired];
  }, [
    author_id,
    selectedPolicyDetails,
    providerPayorCredentialsMapping,
    dateInUtc,
    clinical_relationship,
  ]);

  const isRowDisabled = getIsRowDisabled(
    status,
    readonly,
    providerLicensesLoading,
    providerPayorCredentialsLoading,
  );

  const isSession = procedureCode === ServiceCodes.SessionProcedureCode;
  const showBay = selectedProcedureCode === AllowedServiceCodesToBuildClaim.SessionProcedureCode;
  const procedureCodeValue = isProcedureCode(selectedProcedureCode) ? selectedProcedureCode : null;
  const clinicalRelationship = isClinicalRelationship(clinical_relationship)
    ? clinical_relationship
    : null;

  return (
    <TableRow
      key={id}
      sx={{
        opacity: isRowDisabled ? 0.7 : 1,
        borderLeft: `1px solid ${getAuthorTagBorder(
          clinicalRelationship ?? ClinicalRelationships.Prbt,
        )}`,
        borderRight: `1px solid ${getAuthorTagBorder(
          clinicalRelationship ?? ClinicalRelationships.Prbt,
        )}`,
      }}
      data-testid={`claimrow_${SessionClaimStatus.Unclaimed}_${provider_name.replace(/ /g, '_')}`}
    >
      {showSelectColumn && (
        <TableCell>
          <Box sx={{ display: 'flex', width: 'fit-content' }}>
            <FCheckbox
              data-testid={`selectForClaim_${SessionClaimStatus.Unclaimed}_${provider_name.replace(
                / /g,
                '_',
              )}`}
              iconStyles={{
                opacity: isRowDisabled ? 0.5 : 1,
                width: '25px',
                height: '25px',
              }}
              sx={{
                cursor: isRowDisabled ? 'not-allowed' : 'pointer',
              }}
              colorTheme={Themes.Primary}
              coloredBackground
              disabled={isRowDisabled}
              checked={selected}
              onChange={(e) => onSelectRow(e.target.checked, session)}
            />
            {isSession && clinicalRelationship && (
              <>
                <FHTooltip
                  arrow
                  title={
                    <Typography>{`This session was conducted by client's ${getAuthorTagName(
                      clinicalRelationship,
                    )} - ${session_provider}`}</Typography>
                  }
                  tooltipBorderRadius="8px"
                  tooltipbackground={FORTA_WHITE}
                  placement="top"
                  sx={{
                    display: 'flex',
                    maxWidth: '350px',
                  }}
                  tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                >
                  <Box
                    sx={{
                      px: 1,
                      ml: -1.5,
                      borderRadius: '8px',
                      fontSize: '12px',
                      backgroundColor: getAuthorTagColor(clinicalRelationship),
                      border: `1px solid ${getAuthorTagBorder(
                        clinicalRelationship,
                      )}`,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {getAuthorTagName(clinicalRelationship)}
                  </Box>
                </FHTooltip>
              </>
            )}
          </Box>
        </TableCell>
      )}
      {!historyClaimReviewDialogView && (
        <TableCell
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '21px',
            color: FORTA_GRAY4,
          }}
        >
          <StatusIconRenderer
            disabled={isRowDisabled}
            procedureCode={procedureCode}
            status={sessionStatus}
            isAwaitingFinalization={isAwaitingFinalization}
          />
        </TableCell>
      )}
      <TableCell
        sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          color: FORTA_GRAY4,
        }}
      >
        {moment.utc(dateInUtc).tz('America/Chicago').format('MM/DD/YY')} (
        {toUtcOffsetDisplay(moment.utc(dateInUtc).tz('America/Chicago').utcOffset() * 60)})
      </TableCell>
      <TableCell
        data-testid={`navigateToSession${id}`}
        onClick={() => {
          if (!procedureCodeValue) return;
          const url = getNavigateUrlForProcedures(procedureCodeValue, id);
          if (!url) return;
          window.open(url, '_blank');
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: FORTA_BLUE.primary,
            textDecoration: 'underline',
            cursor: 'pointer',
            width: 'fit-content',
          }}
        >
          {procedureCodeValue ? formatSessionId(procedureCodeValue, id) : null}
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          color: FORTA_GRAY4,
        }}
      >
        <Typography>{provider_name}</Typography>
      </TableCell>
      {!historyClaimReviewDialogView && (
        <TableCell
          sx={{
            fontSize: '14px',
            fontWeight: '700',
            lineHeight: '21px',
            color:
              status === SessionClaimStatus.Unclaimed || status === SessionClaimStatus.NotReady
                ? FORTA_GRAY4
                : FORTA_BLUE.bright,
          }}
        >
          {isUnderClinicalReview ? (
            <FHTooltip
              arrow
              title={
                <Typography>
                  This session is under clinical review. It can be claimed once clinical QA
                  finalizes the changes.
                </Typography>
              }
              placement="top"
              sx={{
                borderRadius: '10px',
                display: 'flex',
                maxWidth: '350px',
              }}
              tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
            >
              <Box
                sx={{
                  display: 'flex',
                  border: `1px solid ${FORTA_GRAY4}`,
                  borderRadius: '8px',
                  borderTopRightRadius: '0px',
                  width: '170px',
                  p: 1,
                  cursor: 'pointer',
                  position: 'relative',
                  backgroundColor: FORTA_GRAY4,
                }}
              >
                <InfoIcon
                  sx={{
                    color: FORTA_ORANGE,
                    mr: 1,
                    mb: -0.5,
                    position: 'absolute',
                    top: -8,
                    right: -18,
                  }}
                />
                <Typography color={FORTA_WHITE}>Under clinical review</Typography>
              </Box>
            </FHTooltip>
          ) : (
            <Typography
              sx={{
                ml: 4,
                px: 1.5,
                py: 0.5,
                textAlign: 'center',
                fontWeight: 700,
                color:
                  Object.entries(ClaimStatusFontColor).find(([key]) => key === status)?.[1] ??
                    FORTA_WHITE,
                borderRadius: '8px',
                minWidth: '100px',
                backgroundColor:
                  Object.entries(ClaimStatusColor).find(([key]) => key === status)?.[1] ??
                    FORTA_ORANGE,
              }}
            >
              {status === SessionClaimStatus.NotReady ? SessionClaimStatus.Unclaimed : status}
            </Typography>
          )}
        </TableCell>
      )}
      {showBay && (
        <TableCell
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '21px',
            textAlign: 'center',
            color: FORTA_GRAY4,
          }}
        >
          <Typography sx={{ ml: -2 }}>{bay}</Typography>
        </TableCell>
      )}
      {!historyClaimReviewDialogView && (
        <>
          <TableCell
            sx={{
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px',
              color: FORTA_GRAY4,
            }}
          >
            <Typography
              sx={
                !isLicenseRequiredInState
                  ? notRequiredStyles
                  : isLicensed
                    ? fulfilledStyles
                    : notFulfilledStyles
              }
            >
              {!isLicenseRequiredInState ? 'NA' : isLicensed ? 'Yes' : 'No'}
            </Typography>
          </TableCell>
          <TableCell
            sx={{
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px',
              color: FORTA_GRAY4,
            }}
          >
            <Typography
              sx={
                !isCredentialRequired
                  ? notRequiredStyles
                  : isCredentialed
                    ? fulfilledStyles
                    : notFulfilledStyles
              }
            >
              {!isCredentialRequired ? 'NA' : isCredentialed ? 'Yes' : 'No'}
            </Typography>
          </TableCell>
          <TableCell
            sx={{
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px',
              textAlign: 'center',
              color: FORTA_GRAY4,
            }}
          >
            <Typography sx={{ ml: -2 }}>{duration / PROCEDURE_DURATION_TO_UNIT_FACTOR}</Typography>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

export default ClaimSessionTableRow;
